import { useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { buildMenu } from '@/app/navigation';
import type { MenuItem } from '@/app/navigation';
import { usePermission } from '@/permissions/usePermission';
import { useAuth } from '@/auth/AuthContext';
import { useSuspiciousTradesBadge } from '@/features/tenant/traderSurveillance/suspiciousTrades/useSuspiciousTradesBadge';
import { usePendingRequestsBadges } from '@/features/tenant/dailyReconciliation/approvalRequests/usePendingRequestsBadges';
import { useTerminalRequestsBadge } from '@/features/host/terminalRequests/useTerminalRequestsBadge';

// Whether `to` corresponds to the current path (exact or a parent of it).
function routeMatches(to: string | undefined, pathname: string): boolean {
  if (!to || to === '#') return false;
  const a = pathname.toLowerCase();
  const b = to.toLowerCase();
  return a === b || a.startsWith(`${b}/`);
}

// True when this item (or any descendant) is the active route — used to expand
// the parent group on load/reload so the active item is visible + highlighted.
function containsActive(item: MenuItem, pathname: string): boolean {
  if (routeMatches(item.to, pathname)) return true;
  return (item.children ?? []).some((c) => containsActive(c, pathname));
}

// Reproduces the real noa sidebar (.app-sidebar / .main-menu / .slide /
// .side-menu__item) driven by the actual ABP menu (admin vs user), with the
// submenu accordion handled in React.
export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const can = usePermission();
  const { user } = useAuth();
  const isHost = !!user?.isAuthenticated && !user.tenantId;
  const isAdmin = (user?.roles ?? []).some((r) => r.toLowerCase() === 'admin');
  const sections = buildMenu(isHost, isAdmin, can);

  // Live pending-alert badge on the Suspicious Trades item (legacy TsAlertsBadge).
  const stCount = useSuspiciousTradesBadge(can('User.TraderSurveillance.SuspiciousTrades'));
  // Live pending-request counts on the Matching Engine / Providers Requests groups.
  const pendingBadges = usePendingRequestsBadges(
    can('User.SafeguardingReconciliation.MatchingEngineRequests.Requests'),
    can('User.SafeguardingReconciliation.ProvidersRequests.Requests'),
  );
  // Host "LP Requests Management" pending-count badge (legacy menu counter).
  const terminalReqCount = useTerminalRequestsBadge(isHost && can('HostPages.TerminalRequestsManagement'));
  const badges: Record<string, number | null> = {
    'suspicious-trades': stCount,
    'terminal-requests': terminalReqCount,
    ...pendingBadges,
  };

  // noa hover-to-expand: when the sidebar is collapsed on desktop
  // (data-toggled="icon-overlay-close"), hovering temporarily expands it.
  const html = () => document.documentElement;
  const onEnter = () => {
    if (html().getAttribute('data-toggled') === 'icon-overlay-close') {
      html().setAttribute('data-icon-overlay', 'open');
    }
  };
  const onLeave = () => html().removeAttribute('data-icon-overlay');

  return (
    <aside className="app-sidebar sticky" id="sidebar" onMouseEnter={onEnter} onMouseLeave={onLeave}>
      <div className="main-sidebar-header">
        <a href="/" className="header-logo">
          <img src="/themes/finkit/logo/logo-horizontal-white.png" alt="logo" className="desktop-logo" />
          <img src="/themes/finkit/logo/logo-white.png" alt="logo" className="toggle-logo" />
          <img src="/themes/finkit/logo/logo-dark.png" alt="logo" className="desktop-dark" />
          <img src="/themes/finkit/logo/logo-dark.png" alt="logo" className="desktop-white" />
          <img src="/themes/finkit/logo/logo-dark.png" alt="logo" className="toggle-white" />
        </a>
      </div>

      <div className="main-sidebar" id="sidebar-scroll">
        <nav className="main-menu-container nav nav-pills flex-column sub-open">
          <ul className="main-menu">
            {sections.map((section, i) => (
              <div key={section.label ?? `s${i}`} style={{ display: 'contents' }}>
                {section.label && (
                  <li className="slide__category">
                    <span className="category-name">{section.label}</span>
                  </li>
                )}
                {section.items.map((item) => (
                  <MenuNode key={item.key} item={item} onNavigate={onNavigate} depth={0} badges={badges} />
                ))}
              </div>
            ))}
          </ul>
        </nav>
      </div>
    </aside>
  );
}

function MenuNode({
  item,
  onNavigate,
  depth,
  badges,
}: {
  item: MenuItem;
  onNavigate?: () => void;
  depth: number;
  badges: Record<string, number | null>;
}) {
  const location = useLocation();
  const hasChildren = !!item.children && item.children.length > 0;
  const activeInside = hasChildren && containsActive(item, location.pathname);
  const [open, setOpen] = useState(() => activeInside);

  // Keep the group expanded whenever the active route is inside it (reload / nav).
  useEffect(() => {
    if (activeInside) setOpen(true);
  }, [activeInside]);

  if (hasChildren) {
    return (
      <li className={`slide has-sub${open ? ' open' : ''}`}>
        <a
          className={`side-menu__item${open || activeInside ? ' active' : ''}`}
          href="#"
          onClick={(e) => {
            e.preventDefault();
            setOpen((v) => !v);
          }}
        >
          {item.icon && <i className={`side-menu__icon ${item.icon}`} />}
          <span className="side-menu__label">{item.label}</span>
          {(badges[item.key] ?? 0) > 0 && (
            <span className="badge bg-danger rounded-circle ms-2" title="Pending">
              {badges[item.key]}
            </span>
          )}
          <i className="fe fe-chevron-right side-menu__angle" />
        </a>
        <ul className={`slide-menu child${depth + 1}${open ? ' active' : ''}`} style={{ display: open ? 'block' : 'none' }}>
          {item.children!.map((child) => (
            <MenuNode key={child.key} item={child} onNavigate={onNavigate} depth={depth + 1} badges={badges} />
          ))}
        </ul>
      </li>
    );
  }

  return (
    <li className="slide">
      <NavLink
        to={item.to ?? '#'}
        onClick={onNavigate}
        className={({ isActive }) => `side-menu__item${isActive ? ' active' : ''}`}
      >
        {item.icon && <i className={`side-menu__icon ${item.icon}`} />}
        <span className="side-menu__label">{item.label}</span>
        {item.live && (
          <span className="badge bg-danger ms-2" style={{ fontSize: '0.6rem' }}>
            Live
          </span>
        )}
        {(badges[item.key] ?? 0) > 0 && (
          <span className="badge bg-danger rounded-circle ms-2" title="Pending alerts">
            {badges[item.key]}
          </span>
        )}
      </NavLink>
    </li>
  );
}
